import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BackendErrorType, isHandledByErrorInterceptor } from 'app/shared/services/errors/backend.errors';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';
import { BannerComponent } from 'app/shared/components/banner/banner.component';
import { ButtonComponent } from 'app/shared/components/button/button.component';
import { LandingpageConfigurationStateService } from 'app/shared/services/state/landingpage-configuration-state.service';
import { AutocompleteValue } from 'app/shared/models/autocomplete-value.model';
import { RequiredFieldsHintComponent } from 'app/shared/components/required-fields-hint/required-fields-hint.component';
import { map } from 'rxjs';

@Component({
  selector: 'ez-personal-access-token',
  imports: [ReactiveFormsModule, MainLayoutComponent, BannerComponent, ButtonComponent, RequiredFieldsHintComponent],
  templateUrl: './personal-access-token.component.html',
  styleUrl: './personal-access-token.component.scss',
})
export class PersonalAccessTokenComponent {
  protected readonly CONTROL_NAME = 'personalAccessToken';

  private readonly landingpageConfigurationStateService = inject(LandingpageConfigurationStateService);
  private readonly router = inject(Router);
  private readonly logger = inject(NGXLogger);

  readonly submitPersonalAccessTokenIsLoading = signal(false);

  readonly form = new FormGroup({
    [this.CONTROL_NAME]: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  readonly errorMessage = toSignal(this.tokenInput.events.pipe(map(() => this.currentErrorMessage())), {
    initialValue: null,
  });

  get tokenInput(): AbstractControl {
    return this.form.controls[this.CONTROL_NAME];
  }

  private currentErrorMessage(): string | null {
    const tokenInput = this.tokenInput;
    if (!tokenInput.invalid || !(tokenInput.dirty || tokenInput.touched)) {
      return null;
    }
    if (tokenInput.errors?.['required']) {
      return 'Fehler: Dies ist ein Pflichtfeld. Bitte geben Sie Ihren Zugangscode ein.';
    }
    if (tokenInput.errors?.[BackendErrorType.CLIENT_ERROR]) {
      return 'Fehler: Bitte überprüfen Sie Ihre Eingabe.';
    }
    return 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
  }

  onSubmit() {
    const personalAccessToken = this.tokenInput.value;

    this.submitPersonalAccessTokenIsLoading.set(true);
    this.landingpageConfigurationStateService
      .fetchOrganisationForToken(personalAccessToken)
      .then(() => {
        this.logger.debug('Submit access token response:', {
          requestParam: personalAccessToken,
        });
        this.router.navigate(['auth']);
      })
      .catch((error) => {
        if (error.message === BackendErrorType.ALREADY_SENT) {
          this.router.navigate(['already-sent'], { queryParams: { code: personalAccessToken } });
        } else if (error.message === BackendErrorType.VERIFICATION_ERROR) {
          this.router.navigate(['verification-error']);
        } else if (!isHandledByErrorInterceptor(error)) {
          this.tokenInput.setErrors({ [error.message]: true });
        }
      })
      .finally(() => {
        this.submitPersonalAccessTokenIsLoading.set(false);
      });
  }

  protected readonly AutocompleteValue = AutocompleteValue;
}
